/**
 * Billing Ledger component.
 *
 * Renders a per-pilot bookkeeping-style transaction list showing debits
 * (billed flights, reshaped into legacy record shape by api.js) and credits
 * (pilot_account_entries / payments) with a running balance.
 *
 * Pure helper functions are exported for testability.
 *
 * @module billing-ledger
 * Requirements: 8.1, 8.2, 8.3, 8.4
 */

import { formatGBP, formatDateUK } from '../utils/format.js';

// ---------------------------------------------------------------------------
// Pure helper functions (exported for testing)
// ---------------------------------------------------------------------------

/**
 * Merge debits (billed flights reshaped to record shape) and pilot_account_entries
 * (credits) into a single array sorted by date ascending.
 *
 * @param {Object[]} debits  - legacy-shaped billing records from api.js (period_start/end = flight_date)
 * @param {Object[]} credits - pilot_account_entries (must have entry_date)
 * @returns {Array<{ date: string, type: 'debit'|'credit', description: string, debit: number, credit: number, raw: Object }>}
 */
export function mergeLedgerEntries(debits, credits) {
  const entries = [];

  for (const d of (debits || [])) {
    const aircraft = d.aircraft?.registration || 'N/A';
    const mins = d.total_billable_mins || 0;
    const amount = Number(d.amount_due) || 0;
    entries.push({
      date: d.period_start || d.period_end || '',
      type: 'debit',
      description: `${aircraft} — ${mins} mins flying`,
      debit: amount,
      credit: 0,
      raw: d,
    });
  }

  for (const c of (credits || [])) {
    const amount = Number(c.amount) || 0;
    const isCredit = c.entry_type === 'CREDIT';
    entries.push({
      date: c.entry_date || '',
      type: isCredit ? 'credit' : 'debit',
      description: c.description || (isCredit ? 'Payment' : 'Charge'),
      debit: isCredit ? 0 : amount,
      credit: isCredit ? amount : 0,
      raw: c,
    });
  }

  entries.sort((a, b) => (a.date || '').localeCompare(b.date || ''));
  return entries;
}

/**
 * Compute a cumulative running balance on each entry.
 * Balance = credits minus debits, accumulated in date order.
 *
 * @param {Array<{ debit: number, credit: number }>} entries - sorted entries
 * @returns {Array<{ ..., balance: number }>} Same entries with `balance` field set
 */
export function computeRunningBalance(entries) {
  let balance = 0;
  return (entries || []).map(entry => {
    balance = balance + (entry.credit || 0) - (entry.debit || 0);
    // Round to 2dp to avoid floating-point drift
    balance = Math.round(balance * 100) / 100;
    return { ...entry, balance };
  });
}

/**
 * Format a ledger entry for display.
 *
 * @param {Object} entry - A merged ledger entry with balance
 * @returns {{ date: string, description: string, debit: string, credit: string, balance: string }}
 */
export function formatLedgerEntry(entry) {
  return {
    date: entry.date ? formatDateUK(entry.date) : '—',
    description: entry.description || '',
    debit: entry.debit ? formatGBP(entry.debit) : '',
    credit: entry.credit ? formatGBP(entry.credit) : '',
    balance: formatGBP(entry.balance ?? 0),
  };
}

// ---------------------------------------------------------------------------
// DOM rendering
// ---------------------------------------------------------------------------

/**
 * Create the billing ledger component.
 *
 * @param {Object} options
 * @param {Object[]} options.debits  - billed flights (legacy record shape) for this pilot
 * @param {Object[]} options.credits - pilot_account_entries for this pilot
 * @param {boolean}  [options.adminMode=false] - If true, show edit controls (future)
 * @returns {HTMLElement}
 */
export function createBillingLedger({ debits, credits, adminMode = false } = {}) {
  const container = document.createElement('div');
  container.className = 'billing-ledger';

  const merged = mergeLedgerEntries(debits, credits);
  const withBalance = computeRunningBalance(merged);

  // Total balance (last entry's balance, or 0)
  const totalBalance = withBalance.length > 0
    ? withBalance[withBalance.length - 1].balance
    : 0;

  // Balance header
  const balanceHeader = document.createElement('div');
  balanceHeader.className = 'billing-ledger__balance';
  const isCredit = totalBalance >= 0;
  balanceHeader.style.cssText = `
    padding: 1rem;
    margin-bottom: 1rem;
    border-radius: 0.5rem;
    background: ${isCredit ? 'var(--color-success-bg, #e8f5e9)' : 'var(--color-danger-bg, #ffebee)'};
    font-size: 1.25rem;
    font-weight: bold;
  `;
  const balanceLabel = isCredit ? 'Credit' : 'Debit';
  const balanceColor = isCredit ? 'var(--color-success, green)' : 'var(--color-danger, red)';
  balanceHeader.innerHTML = `
    Account Balance:
    <span style="color:${balanceColor};">${formatGBP(Math.abs(totalBalance))} (${balanceLabel})</span>
  `;
  container.appendChild(balanceHeader);

  // Transaction table
  if (withBalance.length === 0) {
    const empty = document.createElement('p');
    empty.style.color = 'var(--color-text-muted)';
    empty.textContent = 'No transactions found.';
    container.appendChild(empty);
    return container;
  }

  const table = document.createElement('table');
  table.className = 'billing-ledger__table';
  table.style.cssText = 'width:100%;border-collapse:collapse;';

  // Header
  const thead = document.createElement('thead');
  thead.innerHTML = `
    <tr>
      <th style="text-align:left;padding:0.5rem;border-bottom:2px solid var(--color-border, #ddd);">Date</th>
      <th style="text-align:left;padding:0.5rem;border-bottom:2px solid var(--color-border, #ddd);">Description</th>
      <th style="text-align:right;padding:0.5rem;border-bottom:2px solid var(--color-border, #ddd);">Debit</th>
      <th style="text-align:right;padding:0.5rem;border-bottom:2px solid var(--color-border, #ddd);">Credit</th>
      <th style="text-align:right;padding:0.5rem;border-bottom:2px solid var(--color-border, #ddd);">Balance</th>
    </tr>
  `;
  table.appendChild(thead);

  // Body
  const tbody = document.createElement('tbody');
  for (const entry of withBalance) {
    const formatted = formatLedgerEntry(entry);
    const tr = document.createElement('tr');
    tr.style.borderBottom = '1px solid var(--color-border, #eee)';
    tr.innerHTML = `
      <td style="padding:0.5rem;">${formatted.date}</td>
      <td style="padding:0.5rem;">${formatted.description}</td>
      <td style="text-align:right;padding:0.5rem;color:var(--color-danger, red);">${formatted.debit}</td>
      <td style="text-align:right;padding:0.5rem;color:var(--color-success, green);">${formatted.credit}</td>
      <td style="text-align:right;padding:0.5rem;font-weight:bold;">${formatted.balance}</td>
    `;
    tbody.appendChild(tr);
  }
  table.appendChild(tbody);
  container.appendChild(table);

  return container;
}
